
import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { ArrowLeft, Upload, Camera, Check, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

type DocumentType = 'passport' | 'id-front' | 'id-back' | 'selfie';

const KycVerification: React.FC = () => {
  const { setCurrentScreen, updateKycStatus, currentUser } = useApp();
  const { toast } = useToast();
  const [documents, setDocuments] = useState<Record<DocumentType, string | null>>({
    'passport': null,
    'id-front': null,
    'id-back': null,
    'selfie': null,
  });
  const [documentType, setDocumentType] = useState<'passport' | 'id-card'>('passport');
  const [step, setStep] = useState(1);

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      setCurrentScreen('dashboard');
    }
  };

  const handleSelectDocumentType = (type: 'passport' | 'id-card') => {
    setDocumentType(type);
    setStep(2);
  };

  const handleFileUpload = (type: DocumentType) => {
    // In a real app, this would handle file uploads
    // For this demo, we'll simulate successful uploads
    setDocuments(prev => ({
      ...prev,
      [type]: "uploaded-image-url"
    }));
    
    toast({
      title: "Document uploaded",
      description: "Your document has been successfully uploaded",
    });

    // Determine next step
    if (type === 'passport') {
      setStep(3); // Go to selfie
    } else if (type === 'id-front') {
      setStep(2.5); // Go to ID back
    } else if (type === 'id-back') {
      setStep(3); // Go to selfie
    } else if (type === 'selfie') {
      setStep(4); // Go to review
    }
  };

  const handleSubmit = () => {
    // Validate that all required documents are uploaded
    if (
      (documentType === 'passport' && !documents.passport) ||
      (documentType === 'id-card' && (!documents['id-front'] || !documents['id-back'])) ||
      !documents.selfie
    ) {
      toast({
        title: "Missing documents",
        description: "Please upload all required documents",
        variant: "destructive"
      });
      return;
    }

    // In a real app, this would submit all documents to the backend
    updateKycStatus('pending');
    toast({
      title: "Verification submitted",
      description: "Your identity verification is now under review",
    });
    
    // For demo purposes, auto-approve after 3 seconds
    setTimeout(() => {
      updateKycStatus('approved');
      toast({
        title: "Verification approved",
        description: "Your identity has been verified successfully",
      });
    }, 3000);
    
    setCurrentScreen('dashboard');
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center mb-6 pt-4">
        <button 
          onClick={handleBack}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold ml-4">Identity Verification</h1>
      </div>

      <div className="flex-1 px-4">
        {currentUser?.kycStatus === 'pending' ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="bg-amber-100 p-5 rounded-full mb-6">
              <Check className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Verification in Progress</h2>
            <p className="text-gray-600 mb-6">
              Your documents are under review. This usually takes 1-2 business days.
            </p>
            <button 
              onClick={() => setCurrentScreen('dashboard')} 
              className="btn-primary"
            >
              Back to Dashboard
            </button>
          </div>
        ) : (
          <>
            {/* Step indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-neobank-primary text-white' : 'bg-gray-200'}`}>
                    {step > 1 ? <Check className="w-5 h-5" /> : "1"}
                  </div>
                  <span className="text-xs mt-1">Type</span>
                </div>
                <div className={`flex-1 h-1 mx-2 ${step > 1 ? 'bg-neobank-primary' : 'bg-gray-200'}`}></div>
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-neobank-primary text-white' : 'bg-gray-200'}`}>
                    {step > 2 ? <Check className="w-5 h-5" /> : "2"}
                  </div>
                  <span className="text-xs mt-1">Document</span>
                </div>
                <div className={`flex-1 h-1 mx-2 ${step > 2 ? 'bg-neobank-primary' : 'bg-gray-200'}`}></div>
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-neobank-primary text-white' : 'bg-gray-200'}`}>
                    {step > 3 ? <Check className="w-5 h-5" /> : "3"}
                  </div>
                  <span className="text-xs mt-1">Selfie</span>
                </div>
                <div className={`flex-1 h-1 mx-2 ${step > 3 ? 'bg-neobank-primary' : 'bg-gray-200'}`}></div>
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-neobank-primary text-white' : 'bg-gray-200'}`}>
                    "4"
                  </div>
                  <span className="text-xs mt-1">Review</span>
                </div>
              </div>
            </div>

            {/* Step content */}
            {step === 1 && (
              <div className="animate-fade-in">
                <h2 className="text-xl font-bold mb-4">Select Document Type</h2>
                <p className="text-gray-600 mb-6">
                  Please select the type of document you want to upload for verification.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <button 
                    onClick={() => handleSelectDocumentType('passport')} 
                    className={`p-4 rounded-xl border ${documentType === 'passport' ? 'border-neobank-primary bg-neobank-primary/10' : 'border-gray-200'} flex flex-col items-center`}
                  >
                    <Upload className={`w-8 h-8 mb-2 ${documentType === 'passport' ? 'text-neobank-primary' : 'text-gray-400'}`} />
                    <span className="font-medium">Passport</span>
                  </button>
                  
                  <button 
                    onClick={() => handleSelectDocumentType('id-card')} 
                    className={`p-4 rounded-xl border ${documentType === 'id-card' ? 'border-neobank-primary bg-neobank-primary/10' : 'border-gray-200'} flex flex-col items-center`}
                  >
                    <Upload className={`w-8 h-8 mb-2 ${documentType === 'id-card' ? 'text-neobank-primary' : 'text-gray-400'}`} />
                    <span className="font-medium">ID Card</span>
                    <span className="text-xs text-gray-500">(both sides)</span>
                  </button>
                </div>
              </div>
            )}

            {step === 2 && documentType === 'passport' && (
              <div className="animate-fade-in">
                <h2 className="text-xl font-bold mb-4">Upload Passport</h2>
                <p className="text-gray-600 mb-6">
                  Please upload a clear photo of your passport. Make sure all details are visible.
                </p>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center mb-6">
                  {documents.passport ? (
                    <div className="text-center">
                      <div className="bg-green-100 p-3 rounded-full inline-flex mb-2">
                        <Check className="w-6 h-6 text-green-600" />
                      </div>
                      <p className="font-medium text-green-600">Passport uploaded</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-gray-400 mb-4" />
                      <p className="text-sm text-gray-500 mb-4">Click to take a photo or upload from your device</p>
                    </>
                  )}
                  <button 
                    onClick={() => handleFileUpload('passport')} 
                    className="btn-primary"
                  >
                    {documents.passport ? 'Replace Photo' : 'Upload Passport'}
                  </button>
                </div>
              </div>
            )}

            {step === 2 && documentType === 'id-card' && (
              <div className="animate-fade-in">
                <h2 className="text-xl font-bold mb-4">Upload ID Card (Front)</h2>
                <p className="text-gray-600 mb-6">
                  Please upload a clear photo of the front side of your ID card.
                </p>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center mb-6">
                  {documents['id-front'] ? (
                    <div className="text-center">
                      <div className="bg-green-100 p-3 rounded-full inline-flex mb-2">
                        <Check className="w-6 h-6 text-green-600" />
                      </div>
                      <p className="font-medium text-green-600">ID front uploaded</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-gray-400 mb-4" />
                      <p className="text-sm text-gray-500 mb-4">Click to take a photo or upload from your device</p>
                    </>
                  )}
                  <button 
                    onClick={() => handleFileUpload('id-front')} 
                    className="btn-primary"
                  >
                    {documents['id-front'] ? 'Replace Photo' : 'Upload Front Side'}
                  </button>
                </div>
              </div>
            )}

            {step === 2.5 && documentType === 'id-card' && (
              <div className="animate-fade-in">
                <h2 className="text-xl font-bold mb-4">Upload ID Card (Back)</h2>
                <p className="text-gray-600 mb-6">
                  Now, please upload a clear photo of the back side of your ID card.
                </p>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center mb-6">
                  {documents['id-back'] ? (
                    <div className="text-center">
                      <div className="bg-green-100 p-3 rounded-full inline-flex mb-2">
                        <Check className="w-6 h-6 text-green-600" />
                      </div>
                      <p className="font-medium text-green-600">ID back uploaded</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-gray-400 mb-4" />
                      <p className="text-sm text-gray-500 mb-4">Click to take a photo or upload from your device</p>
                    </>
                  )}
                  <button 
                    onClick={() => handleFileUpload('id-back')} 
                    className="btn-primary"
                  >
                    {documents['id-back'] ? 'Replace Photo' : 'Upload Back Side'}
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="animate-fade-in">
                <h2 className="text-xl font-bold mb-4">Take a Selfie</h2>
                <p className="text-gray-600 mb-6">
                  Please take a clear selfie of your face. Make sure you're in a well-lit area.
                </p>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center mb-6">
                  {documents.selfie ? (
                    <div className="text-center">
                      <div className="bg-green-100 p-3 rounded-full inline-flex mb-2">
                        <Check className="w-6 h-6 text-green-600" />
                      </div>
                      <p className="font-medium text-green-600">Selfie uploaded</p>
                    </div>
                  ) : (
                    <>
                      <Camera className="w-12 h-12 text-gray-400 mb-4" />
                      <p className="text-sm text-gray-500 mb-4">Click to take a selfie or upload a photo</p>
                    </>
                  )}
                  <button 
                    onClick={() => handleFileUpload('selfie')} 
                    className="btn-primary"
                  >
                    {documents.selfie ? 'Replace Selfie' : 'Take Selfie'}
                  </button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="animate-fade-in">
                <h2 className="text-xl font-bold mb-4">Review & Submit</h2>
                <p className="text-gray-600 mb-6">
                  Please review your uploaded documents before submitting.
                </p>

                <div className="mobile-card mb-6">
                  <h3 className="font-medium mb-2">Uploaded Documents</h3>
                  
                  {documentType === 'passport' ? (
                    <div className="flex items-center justify-between py-2">
                      <span>Passport</span>
                      <div className="flex items-center text-green-600">
                        <Check className="w-4 h-4 mr-1" /> Uploaded
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between py-2">
                        <span>ID Card (Front)</span>
                        <div className="flex items-center text-green-600">
                          <Check className="w-4 h-4 mr-1" /> Uploaded
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span>ID Card (Back)</span>
                        <div className="flex items-center text-green-600">
                          <Check className="w-4 h-4 mr-1" /> Uploaded
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="flex items-center justify-between py-2">
                    <span>Selfie</span>
                    <div className="flex items-center text-green-600">
                      <Check className="w-4 h-4 mr-1" /> Uploaded
                    </div>
                  </div>
                </div>

                <button onClick={handleSubmit} className="btn-primary">
                  Submit Verification
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default KycVerification;
